/**
 * Contract state icons — the badge a contract wears in lists, built from the
 * effective states (`EffectiveState`) of its contract files.
 *
 * The lifecycle runs drafting and signing first, then — once signed — the
 * contract's life in time. A state is "abnormal" when it asks the user to act
 * (anything before signing); the signed states are "normal". Two special
 * states sit outside that flow: processing (the file's data is still being
 * extracted) and needs attention (processing is done, but something — a date,
 * a party — is missing and needs a human).
 *
 * The glyphs come from the legacy app (`src/assets/icons/contract_state/`),
 * with their hard-coded colors lifted out so every icon takes three colors:
 * the tile's fill, its border, and the glyph drawn on it. States share colors
 * by group — every gray state is the same gray.
 */
import {
  ContractManagementContractFileType,
  DocumentState,
  NeedsAttentionReason,
  type EffectiveState,
} from '@/api';

/**
 * The backend's severity order, worst first; the order
 * `lowestContractIconState` ranks by, so a contract resolves here exactly as
 * the backend resolves `contract_state` / `amendment_state`.
 */
export const CONTRACT_ICON_STATES = [
  'needs_attention',
  'signature_failed',
  'signature_rejected',
  'signature_expired',
  'processing',
  'in_draft',
  'ready_to_send_for_signature',
  'sending_for_signature',
  'waiting_for_signature',
  'not_yet_active',
  'active',
  'expired',
  'terminated',
  'voided',
] as const satisfies readonly `${EffectiveState}`[];

export type ContractIconState = (typeof CONTRACT_ICON_STATES)[number];

export const CONTRACT_ICON_COLOR_GROUPS = [
  'gray',
  'blue',
  'yellow',
  'red',
  'lightGreen',
  'green',
  'darkGray',
] as const;

export type ContractIconColorGroup = (typeof CONTRACT_ICON_COLOR_GROUPS)[number];

export const contractIconColorGroupLabels: Record<ContractIconColorGroup, string> = {
  gray: 'Gray',
  blue: 'Blue',
  yellow: 'Yellow',
  red: 'Red',
  lightGreen: 'Light green',
  green: 'Green',
  darkGray: 'Dark gray',
};

type Glyph =
  | 'dots'
  | 'pencil'
  | 'signPen'
  | 'hourglass'
  | 'hourglassStill'
  | 'cross'
  | 'warning'
  | 'ban'
  | 'check'
  | 'clock';

export interface ContractIconStateInfo {
  label: string;
  group: ContractIconColorGroup;
  glyph: Glyph;
  /** Abnormal = the user has to act; only abnormal amendments get an overlay. */
  normal: boolean;
  terminal: boolean;
  /** Not a step of the lifecycle, but a condition a file can be in at any point. */
  special: boolean;
  /** What moves a contract into this state. */
  reachedBy: 'user' | 'time' | 'system';
}

export const contractIconStates: Record<ContractIconState, ContractIconStateInfo> = {
  processing: {
    label: 'Processing',
    group: 'yellow',
    glyph: 'dots',
    normal: false,
    terminal: false,
    special: true,
    reachedBy: 'system',
  },
  needs_attention: {
    label: 'Needs attention',
    group: 'yellow',
    glyph: 'warning',
    normal: false,
    terminal: false,
    special: true,
    reachedBy: 'system',
  },
  in_draft: {
    label: 'In draft',
    group: 'gray',
    glyph: 'pencil',
    normal: false,
    terminal: false,
    special: false,
    reachedBy: 'user',
  },
  ready_to_send_for_signature: {
    label: 'Ready to send for signature',
    group: 'blue',
    glyph: 'signPen',
    normal: false,
    terminal: false,
    special: false,
    reachedBy: 'user',
  },
  sending_for_signature: {
    label: 'Sending for signature',
    group: 'yellow',
    glyph: 'hourglass',
    normal: false,
    terminal: false,
    special: false,
    reachedBy: 'user',
  },
  waiting_for_signature: {
    label: 'Waiting for signature',
    group: 'yellow',
    glyph: 'hourglassStill',
    normal: false,
    terminal: false,
    special: false,
    reachedBy: 'user',
  },
  signature_rejected: {
    label: 'Signature rejected',
    group: 'red',
    glyph: 'cross',
    normal: false,
    terminal: true,
    special: false,
    reachedBy: 'user',
  },
  signature_failed: {
    label: 'Signature failed',
    group: 'red',
    glyph: 'cross',
    normal: false,
    terminal: true,
    special: false,
    reachedBy: 'system',
  },
  signature_expired: {
    label: 'Signature expired',
    group: 'red',
    glyph: 'warning',
    normal: false,
    terminal: true,
    special: false,
    reachedBy: 'time',
  },
  not_yet_active: {
    label: 'Not yet effective',
    group: 'lightGreen',
    glyph: 'check',
    normal: true,
    terminal: false,
    special: false,
    reachedBy: 'time',
  },
  active: {
    label: 'Effective',
    group: 'green',
    glyph: 'check',
    normal: true,
    terminal: false,
    special: false,
    reachedBy: 'time',
  },
  expired: {
    label: 'Expired',
    group: 'darkGray',
    glyph: 'clock',
    normal: true,
    terminal: true,
    special: false,
    reachedBy: 'time',
  },
  terminated: {
    label: 'Terminated',
    group: 'darkGray',
    glyph: 'cross',
    normal: true,
    terminal: true,
    special: false,
    reachedBy: 'user',
  },
  voided: {
    label: 'Voided',
    group: 'darkGray',
    glyph: 'ban',
    normal: true,
    terminal: true,
    special: false,
    reachedBy: 'user',
  },
};

/** The worst of several states; null when there are none. */
export function lowestContractIconState(states: ContractIconState[]): ContractIconState | null {
  let lowest: ContractIconState | null = null;
  for (const state of states) {
    if (
      lowest === null ||
      CONTRACT_ICON_STATES.indexOf(state) < CONTRACT_ICON_STATES.indexOf(lowest)
    ) {
      lowest = state;
    }
  }
  return lowest;
}

/** The kind of contract file; appendices don't take part in the icon. */
export type ContractIconLayer = 'contract' | 'amendment';

export interface ContractIconLayers {
  contract: ContractIconState | null;
  amendment: ContractIconState | null;
}

/**
 * What a contract's icon draws, given the states of its contract files.
 *
 * All files of one type resolve to their worst state (the backend does this
 * for real contracts, see `contractIconLayers`).
 */
export function resolveContractIcon(
  files: { layer: ContractIconLayer; state: ContractIconState }[]
): ContractIconLayers {
  const lowest = (layer: ContractIconLayer): ContractIconState | null =>
    lowestContractIconState(files.filter(file => file.layer === layer).map(file => file.state));
  return contractIconLayers({
    contract_state: lowest('contract'),
    amendment_state: lowest('amendment'),
  });
}

/**
 * What a contract's icon draws, given its resolved states. The contract tile
 * is always drawn; the amendment overlay only when the amendments need the
 * user (an abnormal state) — a signed amendment adds nothing to look at.
 */
export function contractIconLayers(
  states: {
    contract_state: ContractIconState | null;
    amendment_state: ContractIconState | null;
  } | null
): ContractIconLayers {
  const amendment = states?.amendment_state ?? null;
  return {
    contract: states?.contract_state ?? null,
    amendment: amendment && !contractIconStates[amendment].normal ? amendment : null,
  };
}

/**
 * Whether a contract file's state is worth showing (and setting). Appendices
 * count towards neither the contract nor the amendment state, so their state
 * means nothing to the user and stays hidden.
 */
export function fileTypeHasState(
  type: ContractManagementContractFileType | null | undefined
): boolean {
  return type !== ContractManagementContractFileType.APPENDIX;
}

/** The label of a state; `none` when there is none. */
export function contractStateLabel(state: ContractIconState | null, none = '—'): string {
  return state ? contractIconStates[state].label : none;
}

/** Labels for the stored `DocumentState` — the effective labels plus "Signed". */
export const documentStateLabels: Record<DocumentState, string> = {
  [DocumentState.NEEDS_ATTENTION]: contractIconStates.needs_attention.label,
  [DocumentState.PROCESSING]: contractIconStates.processing.label,
  [DocumentState.IN_DRAFT]: contractIconStates.in_draft.label,
  [DocumentState.READY_TO_SEND_FOR_SIGNATURE]: contractIconStates.ready_to_send_for_signature.label,
  [DocumentState.SENDING_FOR_SIGNATURE]: contractIconStates.sending_for_signature.label,
  [DocumentState.WAITING_FOR_SIGNATURE]: contractIconStates.waiting_for_signature.label,
  [DocumentState.SIGNED]: 'Signed',
  [DocumentState.SIGNATURE_FAILED]: contractIconStates.signature_failed.label,
  [DocumentState.SIGNATURE_REJECTED]: contractIconStates.signature_rejected.label,
  [DocumentState.SIGNATURE_EXPIRED]: contractIconStates.signature_expired.label,
  [DocumentState.TERMINATED]: contractIconStates.terminated.label,
  [DocumentState.VOIDED]: contractIconStates.voided.label,
};

/** States the backend moves on its own: they can't be set, nor left by hand. */
export const LOCKED_DOCUMENT_STATES: readonly DocumentState[] = [
  DocumentState.PROCESSING,
  DocumentState.SENDING_FOR_SIGNATURE,
  DocumentState.WAITING_FOR_SIGNATURE,
];

export const needsAttentionReasonLabels: Record<NeedsAttentionReason, string> = {
  [NeedsAttentionReason.MANUALLY_SET]: 'Marked as needing attention',
  [NeedsAttentionReason.MISSING_EFFECTIVE_FROM]: 'Signed, but the effective date is missing',
  [NeedsAttentionReason.INVALID_EFFECTIVE_DATES]: 'The effective dates contradict each other',
};

export interface ContractIconColors {
  fill: string;
  border: string;
  glyph: string;
}

export type ContractIconPalette = Record<ContractIconColorGroup, ContractIconColors>;

const WHITE = '#FFFFFF';

export const DEFAULT_CONTRACT_ICON_PALETTE: ContractIconPalette = {
  gray: { fill: '#9FA5AA', border: '#7A8289', glyph: WHITE },
  blue: { fill: '#2E90FA', border: '#1570CD', glyph: WHITE },
  yellow: { fill: '#F88C20', border: '#CF7317', glyph: WHITE },
  red: { fill: '#F04438', border: '#B02C23', glyph: WHITE },
  lightGreen: { fill: '#4CBF3F', border: '#2F9A23', glyph: WHITE },
  green: { fill: '#187F0C', border: '#164F11', glyph: WHITE },
  darkGray: { fill: '#4B5563', border: '#374151', glyph: WHITE },
};

/** A deep copy of a palette (the default one unless given), safe to mutate. */
export function cloneContractIconPalette(
  palette: ContractIconPalette = DEFAULT_CONTRACT_ICON_PALETTE
): ContractIconPalette {
  return JSON.parse(JSON.stringify(palette)) as ContractIconPalette;
}

const hourglassBulb =
  'M16.2764,9.06246c-.3529,1.01374-.9142,1.83194-1.589,2.36064-.7676.6014-1.6897.838-2.6862.571-1.0951-.2934-1.767-.9642-2.10538-1.8478-.30086-.78572-.34607-1.76662-.13341-2.82927L16.2764,9.06246Z';
const hourglassSand =
  'M9.68234,9.91458c.45476.95862,1.26476,1.64962,2.46706,1.87942c1.5506.2965,2.889-.4833,3.7495-1.85182l-6.21656-.0276Z';
const hourglassFlip = 'rotate(180 12.014201 11.97834)';

// The hourglass animation from pending.svg.
const hourglassStyle = `<style>
@keyframes hourglass-flip { 0% { transform: rotate(0deg); } 80% { transform: rotate(0deg); animation-timing-function: cubic-bezier(.77,0,.175,1); } 100% { transform: rotate(180deg); } }
@keyframes hourglass-fill { 0% { transform: translate(8.860872px,12.387387px) scale(.378787,.268281); opacity:.75; animation-timing-function: cubic-bezier(.77,0,.175,1); } 40% { transform: translate(-.5067px,3.8488px) scale(1.045922,.740774); opacity:.75; } 80% { transform: translate(-.5067px,3.8488px) scale(1.045922,.740774); opacity:.75; animation-timing-function: cubic-bezier(.77,0,.175,1); } 100% { transform: translate(-.5067px,3.8488px) scale(1.045922,.740774); opacity:0; } }
@keyframes hourglass-drain { 0% { transform: translate(0,0) scale(1); opacity:.75; animation-timing-function: cubic-bezier(.77,0,.175,1); } 40% { transform: translate(7.6475px,7.50548px) scale(.4021); opacity:.75; } 80% { transform: translate(7.6475px,7.50548px) scale(.4021); opacity:.75; animation-timing-function: cubic-bezier(.77,0,.175,1); } 100% { transform: translate(7.6475px,7.50548px) scale(.4021); opacity:0; } }
@keyframes hourglass-mirror { 0% { opacity:0; } 80% { opacity:0; animation-timing-function: cubic-bezier(.77,0,.175,1); } 100% { opacity:.75; } }
.hourglass-body { animation: hourglass-flip 2500ms linear infinite; transform-box: view-box; transform-origin: 12.014201px 11.97834px; }
.hourglass-fill { animation: hourglass-fill 2500ms linear infinite; transform-box: view-box; transform-origin: 0 0; }
.hourglass-drain { animation: hourglass-drain 2500ms linear infinite; transform-box: view-box; transform-origin: 0 0; }
.hourglass-mirror { animation: hourglass-mirror 2500ms linear infinite; }
@media (prefers-reduced-motion: reduce) { .hourglass-body, .hourglass-fill, .hourglass-drain, .hourglass-mirror { animation: none; } }
</style>`;

/** The hourglass; without its <style> it stays on the first frame, top bulb full. */
function hourglass(c: string, animated: boolean): string {
  return (
    `${
      animated ? hourglassStyle : ''
    }<defs><clipPath id="hourglass-top"><path d="${hourglassBulb}"/></clipPath>` +
    `<clipPath id="hourglass-bottom"><path d="${hourglassBulb}" transform="${hourglassFlip}"/></clipPath></defs>` +
    `<g class="hourglass-body">` +
    `<path d="${hourglassBulb}" stroke="${c}" stroke-linejoin="round"/>` +
    `<path d="${hourglassBulb}" transform="${hourglassFlip}" stroke="${c}" stroke-linejoin="round"/>` +
    `<path d="M8.74219,6.98608L17.3282,9.2867" transform="translate(.005054 0.052267)" stroke="${c}" stroke-linecap="round"/>` +
    `<path d="M8.74219,6.98608L17.3282,9.2867" transform="${hourglassFlip} translate(.005054 0.052267)" stroke="${c}" stroke-linecap="round"/>` +
    `<g clip-path="url(#hourglass-bottom)"><path class="hourglass-fill" d="M7.866849,13.838527L7.721421,14.67l6.624324,2.012199-.30614-2.843673-6.172756.000001Z" transform="matrix(.378787 0 0 0.268281 8.860872 12.387387)" opacity="0.75" fill="${c}"/></g>` +
    `<g clip-path="url(#hourglass-top)"><path class="hourglass-drain" d="${hourglassSand}" opacity="0.75" fill="${c}"/></g>` +
    `<g clip-path="url(#hourglass-bottom)"><path class="hourglass-mirror" d="${hourglassSand}" transform="matrix(-1 0 0-1 24.028402 23.95668)" opacity="0" fill="${c}"/></g>` +
    `</g>`
  );
}

// The four-dot spinner from extracting.svg, scaled from its own 20×20 canvas
// into the tile: the dots rotate and breathe out from the center and back.
const dotsStyle = `<style>
@keyframes dots-rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes dots-top { 0%, 100% { transform: translate(12px, 12px); } 18.5%, 81.5% { transform: translate(12px, 15.3px); } }
@keyframes dots-right { 0%, 100% { transform: translate(12px, 12px); } 18.5%, 81.5% { transform: translate(15.3px, 12px); } }
@keyframes dots-bottom { 0%, 100% { transform: translate(12px, 12px); } 18.5%, 81.5% { transform: translate(12px, 8.7px); } }
@keyframes dots-left { 0%, 100% { transform: translate(12px, 12px); } 18.5%, 81.5% { transform: translate(8.7px, 12px); } }
.dots { animation: dots-rotate 2.7s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite; transform-origin: 12px 12px; }
.dots circle { animation-duration: 2.7s; animation-timing-function: cubic-bezier(0.455, 0.03, 0.195, 0.995); animation-iteration-count: infinite; }
.dots-top { animation-name: dots-top; }
.dots-right { animation-name: dots-right; }
.dots-bottom { animation-name: dots-bottom; }
.dots-left { animation-name: dots-left; }
@media (prefers-reduced-motion: reduce) { .dots, .dots circle { animation: none; } }
</style>`;

const glyphs: Record<Glyph, (color: string) => string> = {
  // Without motion the dots would all sit on the center, so the still frame
  // is laid out spread; the animation's transforms take over when it runs.
  dots: c =>
    `${dotsStyle}<g class="dots">` +
    `<circle class="dots-top" r="1.4" transform="translate(12 15.3)" fill="${c}"/>` +
    `<circle class="dots-right" r="1.4" transform="translate(15.3 12)" fill="${c}" opacity="0.7"/>` +
    `<circle class="dots-bottom" r="1.4" transform="translate(12 8.7)" fill="${c}"/>` +
    `<circle class="dots-left" r="1.4" transform="translate(8.7 12)" fill="${c}" opacity="0.7"/>` +
    `</g>`,
  pencil: c =>
    `<path stroke="${c}" stroke-linecap="round" stroke-linejoin="round" d="m7.556 16.037 2.605-.613 5.697-5.697a.613.613 0 0 0 0-.866l-1.126-1.126a.613.613 0 0 0-.866 0l-5.697 5.697-.613 2.605Zm8.888 0h-3.371"/>`,
  signPen: c =>
    `<path fill="${c}" fill-rule="evenodd" d="M13.685 6.263a.893.893 0 0 0-1.262 0l-1.08 1.08 3.455 3.454 1.08-1.08a.893.893 0 0 0 0-1.262l-2.193-2.192Zm.78 5.414-4.002-4.002-1.005.22c-.4.088-.722.382-.845.773L6.9 14.094a.881.881 0 0 0 0 .54l3.652-3.652a.429.429 0 1 1 .606.606L7.52 15.226a.884.884 0 0 0 .496-.01l5.45-1.688c.393-.122.69-.446.779-.848l.219-1.003Z" clip-rule="evenodd"/>` +
    `<path stroke="${c}" stroke-linecap="round" stroke-width="1.5" d="M7.5 16.44h8"/>`,
  hourglass: c => hourglass(c, true),
  hourglassStill: c => hourglass(c, false),
  check: c =>
    `<circle cx="12" cy="12" r="5.5" stroke="${c}"/>` +
    `<path stroke="${c}" stroke-linecap="round" stroke-linejoin="round" d="m10.2 12.6 1 1.2 2.6-3.6"/>`,
  // Not from the legacy set: drawn in the check's ring so the two read as a family.
  clock: c =>
    `<circle cx="12" cy="12" r="5.5" stroke="${c}"/>` +
    `<path stroke="${c}" stroke-linecap="round" stroke-linejoin="round" d="M12 9.2V12l1.9 1.3"/>`,
  ban: c =>
    `<circle cx="12" cy="12" r="5.5" stroke="${c}"/>` +
    `<path stroke="${c}" stroke-linecap="round" d="m8.2 15.8 7.6-7.6"/>`,
  cross: c =>
    `<path fill="${c}" d="M8.06 15.917a.817.817 0 0 1 0-1.156l6.701-6.701a.817.817 0 0 1 1.156 1.155l-6.701 6.702a.817.817 0 0 1-1.156 0Z"/>` +
    `<path fill="${c}" d="M15.917 15.94a.817.817 0 0 1-1.156 0l-6.7-6.701a.817.817 0 0 1 1.155-1.156l6.7 6.701c.32.32.32.837 0 1.156Z"/>`,
  warning: c =>
    `<path fill="${c}" fill-rule="evenodd" d="m13.925 7.111 3.849 6.667c.855 1.481-.214 3.333-1.925 3.333H8.151c-1.71 0-2.78-1.852-1.924-3.333l3.849-6.667c.855-1.481 2.993-1.481 3.849 0Zm-.962.556a1.111 1.111 0 0 0-1.925 0L7.19 14.333A1.111 1.111 0 0 0 8.152 16h7.698c.856 0 1.39-.926.963-1.667l-3.85-6.666Z" clip-rule="evenodd"/>` +
    `<path fill="${c}" d="m12.459 9.443-.093 3.395H11.5l-.095-3.395h1.054ZM11.97 14.8a.553.553 0 0 1-.402-.166.54.54 0 0 1-.166-.402.53.53 0 0 1 .166-.398.553.553 0 0 1 .402-.166.55.55 0 0 1 .395.166.543.543 0 0 1 .09.684.613.613 0 0 1-.206.206.538.538 0 0 1-.279.076Z"/>`,
};

/**
 * The full 24×24 icon as an SVG string: a white halo (it separates the two
 * tiles where they overlap), the tile in the state's group colors, and the
 * state's glyph.
 */
export function contractStateIconSvg(
  state: ContractIconState,
  palette: ContractIconPalette
): string {
  const { group, glyph } = contractIconStates[state];
  const colors = palette[group];
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">` +
    `<rect width="21" height="21" x="1.5" y="1.5" fill="${WHITE}" stroke="${WHITE}" rx="6.5"/>` +
    `<rect width="19" height="19" x="2.5" y="2.5" fill="${colors.fill}" stroke="${colors.border}" rx="5.5"/>${glyphs[
      glyph
    ](colors.glyph)}</svg>`
  );
}

/**
 * The icon as a data URI for an <img>. An <img> renders the SVG as its own
 * document: the hourglass's clipPath ids can't clash between instances, its CSS
 * animation still runs, and nothing in the markup can script the page.
 */
export function contractStateIconUrl(
  state: ContractIconState,
  palette: ContractIconPalette
): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(contractStateIconSvg(state, palette))}`;
}
